import { useQuery } from "@tanstack/react-query";
import { FileVideo, Plus, Upload, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { formatBytes, isVideo, LARGE_VIDEO_BYTES } from "../lib/media/format";
import { fetchStorageStatus } from "../lib/media/usage";
import { queryKeys } from "../lib/queryKeys";
import { useCloseOnBack } from "../lib/useCloseOnBack";
import TagSheet from "./TagSheet";

interface Props {
  files: File[];
  onRemove: (index: number) => void;
  onCancel: () => void;
  /** 選んだタグ名を添えて送信を始める */
  onStart: (tagNames: string[]) => void;
}

/**
 * 送る前の確認。
 *
 * 選んだ瞬間に送り始めると、取り違えに気づいても止められない。
 * ここで並べて見せ、外すこととタグを決めることをまとめて済ませる。
 * 送信そのものの確認ダイアログは出さない。この画面がその役目を果たす。
 */
export default function UploadSheet({ files, onRemove, onCancel, onStart }: Props) {
  // 戻る操作は「やめる」と同じ扱い。選んだファイルは手放す
  useCloseOnBack(onCancel);
  const [tagNames, setTagNames] = useState<string[]>([]);
  const [tagging, setTagging] = useState(false);

  // プレビューは実体を指す URL。閉じるときに必ず手放す
  const previews = useMemo(
    () => files.map((file) => (isVideo(file) ? null : URL.createObjectURL(file))),
    [files],
  );
  useEffect(() => {
    return () => {
      for (const url of previews) {
        if (url !== null) URL.revokeObjectURL(url);
      }
    };
  }, [previews]);

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const heavy = files.filter((file) => isVideo(file) && file.size > LARGE_VIDEO_BYTES);

  // 容量の残り(上限はカップルごと)。ここで止めるのは体験のためで、
  // 強制は Worker 側の署名チェック
  const storage = useQuery({ queryKey: queryKeys.storageStatus, queryFn: fetchStorageStatus });
  const remaining =
    storage.data === undefined || storage.data === null
      ? null
      : Math.max(0, storage.data.limitBytes - storage.data.usedBytes);
  const overLimit = remaining !== null && totalBytes > remaining;

  return (
    <div className="fixed inset-0 z-55 flex flex-col bg-[var(--color-bg)]">
      <header className="flex items-center justify-between border-b border-[var(--color-line)] p-4">
        <h2 className="text-base font-semibold">
          {files.length}件を追加
          <span className="ml-2 text-xs font-normal text-[var(--color-sub)]">
            {formatBytes(totalBytes)}
          </span>
        </h2>
        <button type="button" onClick={onCancel} aria-label="やめる">
          <X className="size-5 text-[var(--color-sub)]" aria-hidden />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-3 gap-1.5">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${String(file.lastModified)}-${String(index)}`}
              className="relative aspect-square overflow-hidden rounded-md bg-[var(--color-line)]"
            >
              {previews[index] === null || previews[index] === undefined ? (
                <span className="flex size-full flex-col items-center justify-center gap-1 p-1 text-[var(--color-sub)]">
                  <FileVideo className="size-6" aria-hidden />
                  <span className="line-clamp-2 text-[9px] break-all">{file.name}</span>
                </span>
              ) : (
                <img
                  src={previews[index]}
                  alt=""
                  className="size-full object-cover"
                  // HEIC を描けないブラウザがある。名前だけでも出す
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              )}
              <span className="absolute inset-x-0 bottom-0 bg-black/45 py-0.5 text-center text-[9px] text-white">
                {formatBytes(file.size)}
              </span>
              <button
                type="button"
                onClick={() => {
                  onRemove(index);
                }}
                aria-label={`${file.name} を外す`}
                className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-black/55 text-white"
              >
                <X className="size-3" aria-hidden />
              </button>
            </div>
          ))}
        </div>

        {heavy.length > 0 && (
          <p className="pt-3 text-xs text-[var(--color-sub)]">
            大きな動画が{heavy.length}件あります。送信に時間がかかります。
          </p>
        )}

        <section className="flex flex-col gap-2 pt-5">
          <h3 className="text-xs text-[var(--color-sub)]">まとめて付けるタグ</h3>
          <div className="flex flex-wrap items-center gap-1.5">
            {tagNames.map((name) => (
              <span
                key={name}
                className="flex items-center gap-1 rounded-full bg-[var(--color-teal)] px-3 py-1 text-xs text-white"
              >
                {name}
                <button
                  type="button"
                  onClick={() => {
                    setTagNames((current) => current.filter((item) => item !== name));
                  }}
                  aria-label={`${name} を外す`}
                >
                  <X className="size-3" aria-hidden />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={() => {
                setTagging(true);
              }}
              className="flex items-center gap-1 rounded-full border border-[var(--color-line)] px-3 py-1 text-xs text-[var(--color-sub)]"
            >
              <Plus className="size-3" aria-hidden />
              タグ
            </button>
          </div>
        </section>
      </div>

      <div className="border-t border-[var(--color-line)] bg-white p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={() => {
            onStart(tagNames);
          }}
          disabled={files.length === 0 || overLimit}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--color-teal)] py-3 text-sm font-medium text-white disabled:opacity-50"
        >
          <Upload className="size-4" aria-hidden />
          {files.length}件を追加
        </button>
        {remaining !== null && (
          <p
            className={`pt-2 text-center text-xs ${
              overLimit ? "text-[var(--color-plum)]" : "text-[var(--color-sub)]"
            }`}
          >
            {overLimit
              ? `残り容量(${formatBytes(remaining)})を超えています。件数を減らすか、不要なものを削除してください。`
              : `残り容量: ${formatBytes(remaining)} / 上限 ${formatBytes(storage.data?.limitBytes ?? 0)}`}
          </p>
        )}
      </div>

      {tagging && (
        <TagSheet
          count={files.length}
          onPick={(name) => {
            setTagNames((current) => (current.includes(name) ? current : [...current, name]));
            setTagging(false);
          }}
          onClose={() => {
            setTagging(false);
          }}
        />
      )}
    </div>
  );
}
